import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from './useAuth';
import {
  selectAccessContext,
  selectActiveRole,
  selectAvailableRoles,
  selectIsFullAccess,
  switchAdminRole,
} from '../Redux/Slice/adminAccessSlice';
import {
  buildDemoTeamRoles,
  canAccessSidebarItem,
  filterDashboardWidgets,
  filterSidebarLinks,
  FULL_ACCESS_ACCOUNT_ID,
  getModuleAccessFlags,
  getPathModuleAccessFlags,
  hasModuleActionAccess,
  hasModuleViewAccess,
  canAccessAdminPath,
} from '../utils/rolePermissions';
import { getRequiredModuleForAdminPath } from '../utils/adminRoutePermissions';

/**
 * Standard hook for role-based access anywhere in the app.
 * Role data is set on admin login and stored in Redux (adminAccess slice).
 */
export default function useRolePermissions() {
  const dispatch = useDispatch();
  const { setAuth, auth } = useAuth();
  const activeRole = useSelector(selectActiveRole);
  const storedRoles = useSelector(selectAvailableRoles);
  const isSuperAdmin =
    auth?.roleType === 'superadmin' || auth?.user?.roleType === 'superadmin';
  const availableRoles = storedRoles.length
    ? storedRoles
    : isSuperAdmin
      ? buildDemoTeamRoles()
      : [];
  const isFullAccess = useSelector(selectIsFullAccess);
  const accessContext = useSelector(selectAccessContext);

  const switchRole = useCallback(
    (accountId) => {
      dispatch(switchAdminRole({ accountId: accountId ?? FULL_ACCESS_ACCOUNT_ID }));
      try {
        const login = JSON.parse(localStorage.getItem('LoginSession') || 'null');
        if (login) setAuth(login);
      } catch {
        // ignore parse errors
      }
    },
    [dispatch, setAuth],
  );

  const canViewModule = useCallback(
    (moduleKey) => hasModuleViewAccess(activeRole, moduleKey),
    [activeRole],
  );

  const canPerformAction = useCallback(
    (moduleKey, actionKey = 'view') => hasModuleActionAccess(activeRole, moduleKey, actionKey),
    [activeRole],
  );

  const canAccessPath = useCallback(
    (pathname) => canAccessAdminPath(pathname, activeRole),
    [activeRole],
  );

  const getModuleFlags = useCallback(
    (moduleKey) => getModuleAccessFlags(activeRole, moduleKey),
    [activeRole],
  );

  const getPathFlags = useCallback(
    (pathname) => getPathModuleAccessFlags(pathname, activeRole),
    [activeRole],
  );

  const canViewSidebarItem = useCallback(
    (linkName) => canAccessSidebarItem(linkName, accessContext),
    [accessContext],
  );

  const getVisibleSidebarLinks = useCallback(
    (links) => filterSidebarLinks(links, accessContext),
    [accessContext],
  );

  const getVisibleDashboardWidgets = useCallback(
    (widgets) => filterDashboardWidgets(widgets, accessContext),
    [accessContext],
  );

  return {
    activeRole,
    availableRoles,
    isFullAccess,
    accessContext,
    switchRole,
    canViewModule,
    canPerformAction,
    canAccessPath,
    getModuleFlags,
    getPathFlags,
    getRequiredModuleForPath: getRequiredModuleForAdminPath,
    canViewSidebarItem,
    getVisibleSidebarLinks,
    getVisibleDashboardWidgets,
    // Backward-compatible aliases used by admin UI
    activeAccount: activeRole,
    switchAccounts: availableRoles,
    switchAccount: switchRole,
    isOwnerView: isFullAccess,
    activeAccountId: activeRole?.accountId || null,
  };
}
